import { useCallback, useEffect } from "react";

// A draggable vertical divider that resizes a CSS-variable-driven grid column. The variable lives on
// :root so the body grid (--rail-w for the nav rail, --side-w for the app-list bar) reacts live; the
// chosen width is persisted to localStorage and restored on mount.
export function ResizeHandle({
  varName, storageKey, def, min, max, style,
}: {
  varName: string;
  storageKey: string;
  def: number;
  min: number;
  max: number;
  style?: React.CSSProperties;
}) {
  useEffect(() => {
    const saved = Number(localStorage.getItem(storageKey));
    if (Number.isFinite(saved) && saved >= min && saved <= max) {
      document.documentElement.style.setProperty(varName, `${saved}px`);
    }
  }, [varName, storageKey, min, max]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const cur = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      const startW = parseFloat(cur) || def;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      const onMove = (ev: PointerEvent) => {
        const w = Math.min(max, Math.max(min, startW + (ev.clientX - startX)));
        document.documentElement.style.setProperty(varName, `${w}px`);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        const w = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
        localStorage.setItem(storageKey, String(parseFloat(w) || def));
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [varName, def, min, max, storageKey],
  );

  return (
    <div
      className="resizer"
      style={style}
      title="드래그하여 너비 조절 (더블클릭: 기본값)"
      role="separator"
      aria-orientation="vertical"
      onPointerDown={onPointerDown}
      onDoubleClick={() => {
        document.documentElement.style.setProperty(varName, `${def}px`);
        localStorage.setItem(storageKey, String(def));
      }}
    />
  );
}
